import React, { useState, useContext, useCallback, useEffect } from "react";
import LocContext from "../context/locContext";
import axios from "axios";
import Swal from "sweetalert2";
import { data, useNavigate } from "react-router-dom";
import ClientNavbar from "../layout/ClientNavbar";
import Select from "react-select";
import { NavLink } from "react-router-dom";

/* Section Component */
const Section = React.memo(
  ({ step, title, openSection, setOpenSection, activeStep, children }) => {
    const isDisabled = step > activeStep;

    return (
      <div
        className={`border rounded-md mb-3 ${isDisabled ? "opacity-60" : ""}`}
      >
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => {
            if (!isDisabled) setOpenSection(step);
          }}
          className="text-white w-full flex justify-between items-center p-3 font-medium"
          style={{
            background: isDisabled ? "#9ca3af" : "#1bafb4",
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
        >
          <span>
            Step {step}: {title}
          </span>
          <span>{openSection === step ? "−" : "+"}</span>
        </button>

        {openSection === step && <div className="p-4 bg-white">{children}</div>}
      </div>
    );
  },
);

export default function InvestmentPage() {
  const navigate = useNavigate();
  const { userData } = useContext(LocContext);
  const id = userData?.id;

  const [activeStep, setActiveStep] = useState(1);
  const [openSection, setOpenSection] = useState(1);

  const [userDataP, setUserDataP] = useState({});

  // under: const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [attemptedNext, setAttemptedNext] = useState(false);

  const [investment, setInvestment] = useState({
    user_id: userData?.id || "",
    title: "",
    name: "",
    pan: "",
    app_code: "",
    email: "",
    alt_email: "",
    user_name: "",
    country_code: "+91", // country code for mobile
    mobile: "",
    dob: "",
    marital_status: "",
    relationship_manager: "",
    referred_by: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pin: "",
    bank_name: "",
    account_holder_name: "",
    account_no: "",
    account_type: "",
    ifsc: "",
    branch: "",
    nominee_name: "",
    nominee_type: "",
    nominee_dob: "",
    nominee_share: "",
    nominee_relation: "",
    nominee_address: "",
    gross_annual_income: "",
    individual_type: "",
    non_individual_type: "",
    nationality: "",
    gender: "",
    place_of_birth: "",
    country_of_birth: "",
    father_name: "",
    mother_name: "",
    spouse: "",
    ckyc: "",
  });

  const markTouched = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const getUserData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/user/${id}`,
      );
      if (res.status === 200) {
        const user = res.data.data;

        setUserDataP(res.data.data);
        setInvestment({
          user_id: id,
          email: user.email || "",
          pan: user.pan_number || "",
          user_name: user.pan_number || "",
          mobile: user.phone || "",
          name: user.first_name + " " + user.last_name || "",
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getUserData();
  }, []);

  // errors object
  const [errors, setErrors] = useState({});

  const setError = (field, message) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
  };
  const clearError = (field) => {
    setErrors((prev) => {
      const n = { ...prev };
      delete n[field];
      return n;
    });
  };

  // Regex/validators
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/; // Indian PAN
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/; // Indian IFSC
  const numericRegex = /^[0-9]+$/;

  // compute age from DOB (ISO date yyyy-mm-dd)
  const getAge = (dob) => {
    if (!dob) return null;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const validateField = (name, value) => {
    const error = validateSingleField(name, value);

    setErrors((prev) => {
      const updated = { ...prev };

      if (error) {
        updated[name] = error;
      } else {
        delete updated[name];
      }

      return updated;
    });
  };

  const validateSingleField = (name, value) => {
    switch (name) {
      /* ================= STEP 1 ================= */
      case "title":
        if (!value) return "Title is required.";
        break;

      case "name":
        if (!value || value.trim().length < 2)
          return "Please enter a valid name.";
        break;

      case "pan":
        if (!value) return "PAN is required.";
        if (!panRegex.test(value.toUpperCase()))
          return "PAN must be in format AAAAA9999A.";
        break;

      case "user_name":
        if (!value) return "Username is required.";
        break;

      case "email":
        if (!value) return "Email is required.";
        if (!emailRegex.test(value)) return "Invalid email address.";
        break;

      case "mobile":
        if (!value) return "Mobile number is required.";
        if (!numericRegex.test(value))
          return "Mobile must contain digits only.";
        if (investment.country_code === "+91" && value.length !== 10)
          return "Mobile must be 10 digits.";
        break;

      case "dob":
        if (!value) return "DOB is required.";
        if (getAge(value) < 18) return "You must be at least 18 years old.";
        break;

      case "marital_status":
        if (!value) return "Please select marital status.";
        break;

      // case "relationship_manager":
      //   if (!value) return "Relationship manager is required.";
      //   break;

      /* ================= STEP 2 (🔥 FIX HERE) ================= */
      case "country":
        if (!value) return "Country is required.";
        break;

      case "state":
        if (!value) return "State is required.";
        break;

      case "city":
        if (!value) return "City is required.";
        break;

      case "address":
        if (!value || value.trim().length < 5) return "Address is required.";
        break;

      /* ================= STEP 3 ================= */
      case "bank_name":
        if (!value) return "Bank name is required.";
        break;

      case "account_holder_name":
        if (!value) return "Account holder name is required.";
        break;

      case "account_no":
        if (!value) return "Account number is required.";
        if (!numericRegex.test(value)) return "Account number must be numeric.";
        break;

      // case "ifsc":
      //   if (!value) return "IFSC is required.";
      //   if (!ifscRegex.test(value.toUpperCase())) return "Invalid IFSC format.";
      //   break;
      case "ifsc":
        if (!value) return "IFSC is required.";
        break;

      /* ================= STEP 4 ================= */
      case "nominee_name":
        if (!value) return "Nominee name is required.";
        break;

      case "nominee_relation":
        if (!value) return "Nominee relation is required.";
        break;

      case "nominee_share":
        if (!value) return "Nominee share is required.";
        if (Number(value) < 1 || Number(value) > 100)
          return "Nominee share must be between 1 and 100.";
        break;

      /* ================= STEP 5 ================= */
      case "nationality":
        if (!value) return "Nationality is required.";
        break;

      case "father_name":
        if (!value) return "Father name is required.";
        break;

      default:
        return null;
    }

    return null;
  };

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      // update state
      setInvestment((prev) => ({ ...prev, [name]: value }));

      // validation
      validateField(name, value);

      // toggle filled class on the DOM element for immediate styling
      // treat empty string and "-" as empty (you used "-" in some places)
      const trimmed = (value || "").toString().trim();
      if (trimmed !== "" && trimmed !== "-") {
        e.target.classList.add("filled");
      } else {
        e.target.classList.remove("filled");
      }
    },
    [investment.country_code],
  );

  const handleTextOnlyChange = (e) => {
    const { name, value } = e.target;

    // allow only letters and spaces
    const cleanedValue = value.replace(/[^A-Za-z\s]/g, "");

    setInvestment((prev) => ({
      ...prev,
      [name]: cleanedValue,
    }));

    // validate after cleaning
    validateField(name, cleanedValue);
  };

  const handleNumberOnlyChange = (e) => {
    const { name, value } = e.target;

    // allow only digits
    const cleaned = value.replace(/\D/g, "");

    setInvestment((prev) => ({
      ...prev,
      [name]: cleaned,
    }));

    validateField(name, cleaned);
  };

  // dependency only for country_code usage in validateField

  // helper: set .filled on inputs which have real value already (skip "-")
  // helper: set .filled on inputs/selects which have real value already (skip "-")
  const updateFilledClasses = () => {
    // scope to the current form to avoid unintended matches
    const form = document.querySelector("form");
    if (!form) return;

    // inputs and selects inside the form
    form.querySelectorAll("input, select, textarea").forEach((el) => {
      try {
        // for controlled inputs value is on `el.value`
        const val = (el.value || "").toString().trim();
        if (val !== "" && val !== "-") el.classList.add("filled");
        else el.classList.remove("filled");
      } catch (err) {
        // ignore elements without value property
      }
    });

    // react-select single-value elements (there can be multiple)
    document.querySelectorAll(".react-select__single-value").forEach((el) => {
      if (el.textContent && el.textContent.trim() !== "") {
        el.classList.add("filled");
      } else {
        el.classList.remove("filled");
      }
    });

    // also handle react-select input (when the control shows placeholder)
    document.querySelectorAll(".react-select__control").forEach((ctrl) => {
      // find single value inside
      const sv = ctrl.querySelector(".react-select__single-value");
      if (sv && sv.textContent.trim() !== "") {
        ctrl.classList.add("filled");
      } else {
        ctrl.classList.remove("filled");
      }
    });
  };

  // run when investment/userDataP loads/updates
  useEffect(() => {
    // small timeout to ensure DOM updated after React render
    setTimeout(updateFilledClasses, 0);
  }, [investment, userDataP]);

  // run when investment/userDataP loads/updates (already present) AND when user navigates sections
  useEffect(() => {
    // small timeout so DOM is painted before we toggle classes
    const t = setTimeout(() => updateFilledClasses(), 20);
    return () => clearTimeout(t);
  }, [investment, userDataP, openSection, activeStep]);

  // On blur special behaviours (uppercase PAN, IFSC)
  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "pan" && value) {
      const v = value.toUpperCase();
      setInvestment((prev) => ({ ...prev, pan: v }));
      validateField("pan", v);
    }
    if (name === "ifsc" && value) {
      const v = value.toUpperCase();
      setInvestment((prev) => ({ ...prev, ifsc: v }));
      validateField("ifsc", v);
    }
    if (name === "dob") validateField("dob", value);
  };

  // Validate entire step: returns boolean
  const validateStep = (step) => {
    let stepErrors = {};

    const check = (field) => {
      const error = validateSingleField(field, investment[field]);
      if (error) stepErrors[field] = error;
    };

    if (step === 1) {
      [
        "title",
        "name",
        "pan",
        "user_name",
        "email",
        "mobile",
        "dob",
        "marital_status",
        "relationship_manager",
      ].forEach(check);
    }

    if (step === 2) {
      ["country", "state", "city", "address"].forEach(check);
    }

    if (step === 3) {
      ["bank_name", "account_holder_name", "account_no", "ifsc"].forEach(check);
    }

    if (step === 4) {
      ["nominee_name", "nominee_relation"].forEach(check);
    }

    if (step === 5) {
      ["nationality", "father_name"].forEach(check);
    }

    // 🔥 IMPORTANT: REPLACE errors completely
    setErrors(stepErrors);

    return Object.keys(stepErrors).length === 0;
  };

  const handleNext = () => {
    setAttemptedNext(true);

    const isValid = validateStep(activeStep);

    if (!isValid) {
      Swal.fire(
        "Incomplete Step",
        "Please fill all required fields before proceeding.",
        "warning",
      );
      return; // 🚫 HARD STOP
    }

    setAttemptedNext(false);
    const next = activeStep + 1;
    setActiveStep(next);
    setOpenSection(next);

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   for (let step = 1; step <= 5; step++) {
  //     if (!validateStep(step)) {
  //       setActiveStep(step);
  //       setOpenSection(step);
  //       Swal.fire(
  //         "Incomplete Form",
  //         "Please complete all steps before submitting.",
  //         "warning",
  //       );
  //       return;
  //     }
  //   }

  //   try {
  //     await axios.post(
  //       `${process.env.REACT_APP_BACKEND_BASE_URL}/investment/store`,
  //       investment,
  //     );

  //     Swal.fire({
  //       icon: "success",
  //       title: "Investment saved successfully",
  //       text: "Your account will be activated within 24 hours.",
  //     }).then(() => navigate("/overview"));
  //   } catch (err) {
  //     Swal.fire("Error", "Failed to save investment", "error");
  //   }
  // };

  // small helper to render error text

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (let step = 1; step <= 5; step++) {
      if (!validateStep(step)) {
        setActiveStep(step);
        setOpenSection(step);
        Swal.fire({
          icon: "warning",
          title: "Incomplete Form",
          text: "Please complete all steps before submitting.",
          timer: 1500,
          showConfirmButton: false,
        });
        return;
      }
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/investment/store`,
        investment,
      );

      Swal.fire({
        icon: "success",
        title: "Investment saved successfully",
        text: "Your account will be activated within 24 hours.",
        timer: 1000, // ⏱ 1 second
        showConfirmButton: false, // ❌ No OK button
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => {
        navigate("/overview"); // redirect after popup closes
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save investment",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const ErrorText = ({ field }) => {
    const show = errors[field] && (touched[field] || attemptedNext);
    return show ? (
      <p className="text-sm text-red-600 mt-1">{errors[field]}</p>
    ) : null;
  };

  // country code options (extend as needed)
  const countryCodes = [
    { code: "+91", label: "India (+91)" },
    { code: "+1", label: "USA (+1)" },
    { code: "+44", label: "UK (+44)" },
    { code: "+61", label: "Australia (+61)" },
  ];

  // inside the component, near handleChange / validators
  const indianStates = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
    "Delhi",
    "Jammu and Kashmir",
    "Ladakh",
  ];

  const handleCountryChange = (e) => {
    const { name, value } = e.target;
    // update country, and if switching away from India clear the state value
    setInvestment((prev) => ({
      ...prev,
      [name]: value,
      state: value === "India" ? prev.state : "", // keep state only when India; clear otherwise
    }));
    // validate country if you need (not required)
    // also clear state error when switching to India (we'll keep validateField for state)
    if (value !== "India") clearError("state");
  };

  useEffect(() => {
    // only validate marital_status after user interacted with it or attempted to move next
    if (
      (touched.marital_status || attemptedNext) &&
      investment.marital_status !== undefined
    ) {
      validateField("marital_status", investment.marital_status);
    }
  }, [investment.marital_status, touched.marital_status, attemptedNext]);

  const nomineeRelations = [
    "Spouse",
    "Son",
    "Daughter",
    "Father",
    "Mother",
    "Brother",
    "Sister",
    "Friend",
    "Other",
  ];

  const rmOptions = [
    { value: "test1", label: "test1" },
    { value: "test2", label: "test2" },
    { value: "Ramesh", label: "Ramesh" },
    { value: "Suresh", label: "Suresh" },
    { value: "Pooja", label: "Pooja" },
    // ...more
  ];

  return (
    <>
      <style>{`
/* ================== FILLED INPUT STYLING ================== */
input.filled,
select.filled,
textarea.filled,
.react-select__single-value.filled,
.react-select__control.filled {
  font-weight: 100 !important;
  color: #000 !important;
}

.w-full.border.filled,
input.filled,
textarea.filled,
select.filled { 
  color: #000 !important;
  font-weight: 200 !important;
}

/* placeholders */
input::placeholder,
textarea::placeholder,
select::placeholder {
  font-weight: 400;
  color: #777;
}

/* ================== DISABLED FIELD STYLING (100% FIXED) ================== */
input[disabled],
select[disabled],
textarea[disabled],
.form-control[disabled],
.w-full.border[disabled] {
  background-color: #f5f5f5 !important;
  color: #666666 !important;
  font-weight: 600 !important;
  cursor: not-allowed !important;
  opacity: 1 !important;
  -webkit-text-fill-color: #666666 !important; /* Safari fix */
}

/* country code select inside phone-row */
.phone-row .country-select[disabled] {
  background-color: #f5f5f5 !important;
  color: #666666 !important;
  font-weight: 600 !important;
  opacity: 1 !important;
}

/* Bootstrap fallback */
.form-control:disabled {
  background-color: #f5f5f5 !important;
  color: #666666 !important;
  font-weight: 600 !important;
  opacity: 1 !important;
}

/* react-select disabled */
.react-select__control--is-disabled {
  background-color: #f5f5f5 !important;
  border-color: #d1d5db !important;
  color: #666666 !important;
  cursor: not-allowed !important;
  opacity: 1 !important;
}

.react-select__control--is-disabled .react-select__single-value,
.react-select__control--is-disabled .react-select__placeholder {
  color: #666666 !important;
  font-weight: 600 !important;
}

/* prevent filled styling overriding disabled */
input[disabled].filled,
select[disabled].filled,
textarea[disabled].filled {
  color: #666666 !important;
  font-weight: 600 !important;
}

/* disable focus effect */
input[disabled]:focus,
select[disabled]:focus,
textarea[disabled]:focus {
  outline: none !important;
  box-shadow: none !important;
}

/* ================== LABELS, ERROR & GENERAL UI ================== */
h3 a:hover {
  text-decoration: underline;
}

label {
  font-weight: 600;
  color: #1E2E50;
}

label span.required {
  color: red;
  margin-left: 2px;
}

.error-input {
  border-color: #dc2626 !important;
}

/* ================== PHONE ROW LAYOUT ================== */
.phone-row {
  display: flex;
  gap: 8px;
  flex-direction: row;
  align-items: center;
}

.phone-row .country-select {
  min-width: 150px;
}

.phone-row .phone-input {
  flex: 1;
}

/* ================== RESPONSIVE ================== */
@media (max-width: 767px) {
  .phone-row {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }

  .phone-row .country-select,
  .phone-row .phone-input {
    width: 100%;
    min-width: 0;
  }
}

  
      `}</style>

      <ClientNavbar />
      <div className="page-content" style={{ background: "#f1f5f9" }}>
            <div className="card shadow-sm p-4 mb-4">
              <h3 className="text-primary mb-3">Mutual Fund Investment</h3>
              {/* Breadcrumb with custom link color */}
              <nav
                aria-label="breadcrumb"
                style={{ "--bs-breadcrumb-divider": "none" }}
              >
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <NavLink to="/overview">Dashboard</NavLink> /
                  </li>
                  <li className="breadcrumb-item" aria-current="page">
                    <NavLink to="/clientmutualfunds">
                      Available Mutual Funds
                    </NavLink>{" "}
                    /
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Mutual Fund Investment
                  </li>
                </ol>
              </nav>
            </div>

            <div className="card shadow-sm p-4 mb-4">
              <form
                onSubmit={handleSubmit}
                autoComplete="off"
                className="space-y-4"
              >
                {/* Step 1: Personal Information */}
                <Section
                  step={1}
                  title="Personal Information"
                  openSection={openSection}
                  setOpenSection={setOpenSection}
                  activeStep={activeStep}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Title */}
                    <div>
                      <label>
                        Title<span className="required">*</span>
                      </label>
                      <select
                        name="title"
                        value={investment.title}
                        onChange={handleChange}
                        className={`w-full border p-2 rounded ${
                          errors.title ? "error-input" : ""
                        }`}
                      >
                        <option value="">Select Title</option>
                        <option value="Mr">Mr</option>
                        <option value="Ms">Ms</option>
                        <option value="Miss">Mrs</option>
                      </select>
                      <ErrorText field="title" />
                    </div>

                    {/* Name */}
                    <div>
                      <label>
                        Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        placeholder="Enter Your Name"
                        value={investment.name}
                        // onChange={handleChange}
                        disabled
                        onBlur={(e) => validateField("name", e.target.value)}
                        className={`w-full border p-2 rounded ${
                          errors.name ? "error-input" : ""
                        }`}
                      />
                      <ErrorText field="name" />
                    </div>

                    {/* PAN */}
                    <div>
                      <label>
                        PAN NO. <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="pan"
                        value={investment.pan}
                        // onChange={handleChange}
                        disabled
                        onBlur={handleBlur}
                        className={`w-full border p-2 rounded ${
                          errors.pan ? "error-input" : ""
                        }`}
                        placeholder="Enter Your Pan No."
                      />
                      <ErrorText field="pan" />
                    </div>

                    {/* App Code */}
                    <div>
                      <label>App Code</label>
                      <input
                        type="text"
                        name="app_code"
                        placeholder="Enter Your App Code"
                        value={investment.app_code}
                        disabled
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label>
                        Email <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={investment.email}
                        // onChange={handleChange}
                        disabled
                        onBlur={(e) => validateField("email", e.target.value)}
                        className={`w-full border p-2 rounded ${
                          errors.email ? "error-input" : ""
                        }`}
                        placeholder="Enter Your Email"
                      />
                      <ErrorText field="email" />
                    </div>

                    {/* Alternate Email */}
                    <div>
                      <label>Alternate Email</label>
                      <input
                        type="email"
                        name="alt_email"
                        value={investment.alt_email}
                        onChange={handleChange}
                        onBlur={(e) =>
                          validateField("alt_email", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.alt_email ? "error-input" : ""
                        }`}
                        placeholder="Enter Your Alternate Email"
                      />
                      <ErrorText field="alt_email" />
                    </div>

                    {/* Username */}
                    <div>
                      <label>
                        Username <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        name="user_name"
                        placeholder="Enter Your Username"
                        value={investment.pan}
                        // onChange={handleChange}
                        disabled
                        onBlur={(e) =>
                          validateField("user_name", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.user_name ? "error-input" : ""
                        }`}
                      />
                      <ErrorText field="user_name" />
                    </div>

                    {/* Mobile with country code */}
                    <div>
                      <label>
                        Mobile <span className="required">*</span>
                      </label>
                      <div className="phone-row">
                        <select
                          name="country_code"
                          value={investment.country_code}
                          disabled
                          className="country-select border p-2 rounded"
                        >
                          {countryCodes.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.label}
                            </option>
                          ))}
                        </select>

                        <input
                          type="tel"
                          name="mobile"
                          value={investment.mobile}
                          disabled
                          onBlur={(e) =>
                            validateField("mobile", e.target.value)
                          }
                          className={`phone-input border p-2 rounded ${
                            errors.mobile ? "error-input" : ""
                          }`}
                          placeholder="Enter Your Mobile No."
                        />
                      </div>
                      <ErrorText field="mobile" />
                    </div>

                    {/* DOB */}
                    <div>
                      <label>
                        DOB <span className="required">*</span>
                      </label>
                      <input
                        type="date"
                        name="dob"
                        value={investment.dob}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full border p-2 rounded ${
                          errors.dob ? "error-input" : ""
                        }`}
                      />
                      <ErrorText field="dob" />
                    </div>

                    {/* Marital Status */}
                    <div>
                      <label>
                        Marital Status <span className="required">*</span>
                      </label>
                      <select
                        name="marital_status"
                        value={investment.marital_status}
                        onChange={(e) => {
                          handleChange(e);
                          setTouched((prev) => ({
                            ...prev,
                            marital_status: true,
                          }));
                        }}
                        onBlur={(e) => {
                          markTouched("marital_status");
                          validateField("marital_status", e.target.value);
                        }}
                        className={`w-full border p-2 rounded ${
                          errors.marital_status &&
                          (touched.marital_status || attemptedNext)
                            ? "error-input"
                            : ""
                        }`}
                      >
                        <option value="">Select Marital Status</option>
                        <option value="Married">Married</option>
                        <option value="Unmarried">Unmarried</option>
                      </select>

                      <ErrorText field="marital_status" />
                    </div>

                    {/* Relationship Manager */}
                    <div>
                      <label>Relationship Manager</label>
                      <Select
                        name="relationship_manager"
                        // find the selected option object from the options array
                        value={
                          rmOptions.find(
                            (opt) =>
                              opt.value === investment.relationship_manager,
                          ) || null
                        }
                        onChange={(selected) => {
                          const val = selected ? selected.value : "";
                          // update form state
                          setInvestment((prev) => ({
                            ...prev,
                            relationship_manager: val,
                          }));
                          // clear validation error immediately
                          clearError("relationship_manager");
                          // optionally re-validate this field:
                          // validateField("relationship_manager", val);
                        }}
                        options={rmOptions}
                        placeholder="Search or select Relationship Manager"
                        isClearable
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: errors.relationship_manager
                              ? "#dc2626"
                              : "#d1d5db",
                            minHeight: "42px",
                            fontSize: "1rem",
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                        }}
                      />
                      <ErrorText field="relationship_manager" />
                    </div>

                    {/* Referred By */}
                    <div>
                      <label>Referred By</label>
                      <input
                        type="text"
                        name="referred_by"
                        placeholder="Enter Your Referred By"
                        value={investment.referred_by}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                      />
                    </div>
                  </div>

                  {activeStep === 1 && (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="btn btn-primary mt-3"
                    >
                      Next
                    </button>
                  )}
                </Section>

                {/* Step 2: Residential Address */}
                <Section
                  step={2}
                  title="Residential Address"
                  openSection={openSection}
                  setOpenSection={setOpenSection}
                  activeStep={activeStep}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Co
                  untry dropdown */}
                    <div>
                      <label>
                        Country <span className="required">*</span>
                      </label>
                      <select
                        name="country"
                        value={investment.country}
                        onChange={handleCountryChange}
                        onBlur={(e) => validateField("country", e.target.value)}
                        className={`w-full border p-2 rounded ${
                          errors.country ? "error-input" : ""
                        }`}
                      >
                        <option value="">Select Country</option>
                        <option value="India">India</option>
                        <option value="USA">USA</option>
                        <option value="Japan">Japan</option>
                        <option value="Australia">Australia</option>
                        <option value="United Kingdom">United Kingdom</option>
                        {/* add more countries as needed */}
                      </select>
                      <ErrorText field="country" />
                    </div>

                    {/* State: conditional select when India, otherwise text input */}
                    <div>
                      <label>
                        State <span className="required">*</span>
                      </label>

                      {investment.country === "India" ? (
                        /* ✅ DROPDOWN FOR INDIA */
                        <select
                          name="state"
                          value={investment.state}
                          onChange={(e) => {
                            setInvestment((prev) => ({
                              ...prev,
                              state: e.target.value,
                            }));
                            validateField("state", e.target.value);
                          }}
                          onBlur={(e) => validateField("state", e.target.value)}
                          className={`w-full border p-2 rounded ${
                            errors.state ? "error-input" : ""
                          }`}
                        >
                          <option value="">Select State</option>
                          {indianStates.map((s) => (
                            <option key={s} value={s}>
                              {s}
                            </option>
                          ))}
                        </select>
                      ) : (
                        /* ✅ TEXT INPUT FOR OTHER COUNTRIES */
                        <input
                          type="text"
                          name="state"
                          value={investment.state}
                          onChange={handleTextOnlyChange} // 🔥 TEXT ONLY
                          onBlur={(e) => validateField("state", e.target.value)}
                          className={`w-full border p-2 rounded ${
                            errors.state ? "error-input" : ""
                          }`}
                          placeholder="Enter state / region"
                          inputMode="text"
                        />
                      )}

                      <ErrorText field="state" />
                    </div>

                    {/* City */}
                    <div>
                      <label>
                        City <span className="required">*</span>
                      </label>

                      <input
                        type="text"
                        name="city"
                        value={investment.city}
                        onChange={handleTextOnlyChange}
                        onBlur={(e) => validateField("city", e.target.value)}
                        className={`w-full border p-2 rounded ${
                          errors.city ? "error-input" : ""
                        }`}
                        placeholder="Enter city"
                        inputMode="text"
                      />

                      <ErrorText field="city" />
                    </div>

                    {/* Address */}
                    <div>
                      <label>
                        Address <span className="required">*</span>
                      </label>
                      <input
                        name="address"
                        value={investment.address}
                        onChange={handleChange}
                        onBlur={(e) => validateField("address", e.target.value)}
                        className={`w-full border p-2 rounded ${
                          errors.address ? "error-input" : ""
                        }`}
                        placeholder="Enter your address"
                      />
                      <ErrorText field="address" />
                    </div>

                    {/* Pin */}
                    <div>
                      <label>Pin Code</label>
                      <input
                        name="pin"
                        value={investment.pin}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                        type="number"
                        placeholder="Enter your Pin Code"
                      />
                    </div>
                  </div>

                  {activeStep === 2 && (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="btn btn-primary mt-3"
                    >
                      Next
                    </button>
                  )}
                </Section>

                {/* Step 3: Bank Details */}
                <Section
                  step={3}
                  title="Bank Details"
                  openSection={openSection}
                  setOpenSection={setOpenSection}
                  activeStep={activeStep}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label>
                        Bank Name <span className="required">*</span>
                      </label>
                      <input
                        name="bank_name"
                        value={investment.bank_name}
                        onChange={handleTextOnlyChange}
                        onBlur={(e) =>
                          validateField("bank_name", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.bank_name ? "error-input" : ""
                        }`}
                        placeholder="e.g., State Bank of India"
                        inputMode="text"
                      />
                      <ErrorText field="bank_name" />
                    </div>

                    <div>
                      <label>
                        Account Holder Name <span className="required">*</span>
                      </label>

                      <input
                        type="text"
                        name="account_holder_name"
                        value={investment.account_holder_name}
                        onChange={handleTextOnlyChange}
                        onBlur={(e) =>
                          validateField("account_holder_name", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.account_holder_name ? "error-input" : ""
                        }`}
                        placeholder="Full name as on bank account"
                        inputMode="text"
                      />

                      <ErrorText field="account_holder_name" />
                    </div>

                    <div>
                      <label>
                        Account No <span className="required">*</span>
                      </label>

                      <input
                        type="text"
                        name="account_no"
                        value={investment.account_no}
                        onChange={handleNumberOnlyChange}
                        onBlur={(e) =>
                          validateField("account_no", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.account_no ? "error-input" : ""
                        }`}
                        placeholder="Enter account number"
                        inputMode="numeric"
                        maxLength={18}
                      />

                      <ErrorText field="account_no" />
                    </div>

                    <div>
                      <label>Account Type</label>
                      <select
                        name="account_type"
                        value={investment.account_type}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                      >
                        <option value="">Select Account Type</option>
                        <option value="Savings">Savings</option>
                        <option value="Current">Current</option>
                      </select>
                    </div>

                    <div>
                      <label>
                        IFSC <span className="required">*</span>
                      </label>

                      <input
                        name="ifsc"
                        value={investment.ifsc}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`w-full border p-2 rounded ${
                          errors.ifsc ? "error-input" : ""
                        }`}
                        placeholder="Enter IFSC code"
                      />

                      <ErrorText field="ifsc" />
                    </div>

                    <div>
                      <label>Branch</label>

                      <input
                        type="text"
                        name="branch"
                        value={investment.branch}
                        onChange={handleTextOnlyChange}
                        className="w-full border p-2 rounded"
                        placeholder="Branch name"
                        inputMode="text"
                      />
                    </div>
                  </div>

                  {activeStep === 3 && (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="btn btn-primary mt-3"
                    >
                      Next
                    </button>
                  )}
                </Section>

                {/* Step 4: Nominee Details */}
                <Section
                  step={4}
                  title="Nominee Details"
                  openSection={openSection}
                  setOpenSection={setOpenSection}
                  activeStep={activeStep}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label>
                        Nominee Name <span className="required">*</span>
                      </label>
                      <input
                        name="nominee_name"
                        value={investment.nominee_name}
                        onChange={handleTextOnlyChange}
                        onBlur={(e) =>
                          validateField("nominee_name", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.nominee_name ? "error-input" : ""
                        }`}
                        placeholder="Full name of nominee"
                        inputMode="text"
                      />
                      <ErrorText field="nominee_name" />
                    </div>

                    <div>
                      <label>Nominee Type</label>
                      <select
                        name="nominee_type"
                        value={investment.nominee_type || ""}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                      >
                        <option value="">Select Nominee Type</option>
                        <option value="Individual">Individual</option>
                        <option value="Organization">Organization</option>
                      </select>
                    </div>

                    <div>
                      <label>Nominee DOB</label>
                      <input
                        type="date"
                        name="nominee_dob"
                        value={investment.nominee_dob}
                        onChange={(e) => {
                          // keep value and validate
                          handleChange(e);
                        }}
                        onBlur={(e) =>
                          validateField("nominee_dob", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.nominee_dob ? "error-input" : ""
                        }`}
                      />
                      <ErrorText field="nominee_dob" />
                    </div>

                    <div>
                      <label>
                        Nominee Share (%) <span className="required">*</span>
                      </label>

                      <input
                        name="nominee_share"
                        value={investment.nominee_share}
                        onChange={(e) => {
                          // allow digits only
                          let v = e.target.value.replace(/\D/g, "");

                          // limit to 3 digits max (100)
                          if (v.length > 3) return;

                          // prevent values > 100
                          if (Number(v) > 100) return;

                          setInvestment((prev) => ({
                            ...prev,
                            nominee_share: v,
                          }));

                          // live validation
                          validateField("nominee_share", v);
                        }}
                        onBlur={(e) =>
                          validateField("nominee_share", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.nominee_share ? "error-input" : ""
                        }`}
                        placeholder="1 to 100"
                        inputMode="numeric"
                      />

                      <ErrorText field="nominee_share" />
                    </div>

                    <div>
                      <label>
                        Nominee Relation <span className="required">*</span>
                      </label>
                      <select
                        name="nominee_relation"
                        value={investment.nominee_relation}
                        onChange={(e) => {
                          handleChange(e);
                          validateField("nominee_relation", e.target.value);
                        }}
                        onBlur={(e) =>
                          validateField("nominee_relation", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.nominee_relation ? "error-input" : ""
                        }`}
                      >
                        <option value="">Select Relation</option>
                        {nomineeRelations.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      <ErrorText field="nominee_relation" />
                    </div>

                    <div>
                      <label>Nominee Address</label>
                      <input
                        name="nominee_address"
                        value={investment.nominee_address}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                        placeholder="Nominee address (optional)"
                      />
                    </div>
                  </div>

                  {activeStep === 4 && (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="btn btn-primary mt-3"
                    >
                      Next
                    </button>
                  )}
                </Section>

                {/* Step 5: KYC & FATCA */}
                <Section
                  step={5}
                  title="KYC & FATCA"
                  openSection={openSection}
                  setOpenSection={setOpenSection}
                  activeStep={activeStep}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label>Gross Annual Income</label>
                      <input
                        name="gross_annual_income"
                        value={investment.gross_annual_income}
                        onChange={(e) => {
                          // allow digits and dot and commas while typing
                          const v = e.target.value.replace(/[^\d.]/g, "");
                          setInvestment((prev) => ({
                            ...prev,
                            gross_annual_income: v,
                          }));
                          // live validation
                          validateField("gross_annual_income", v);
                        }}
                        onBlur={(e) =>
                          validateField("gross_annual_income", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.gross_annual_income ? "error-input" : ""
                        }`}
                        placeholder="e.g., 500000"
                        inputMode="numeric"
                      />
                      <ErrorText field="gross_annual_income" />
                    </div>

                    <div>
                      <label>Individual Type</label>
                      <select
                        name="individual_type"
                        value={investment.individual_type}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                      >
                        <option value="">Select Individual Type</option>
                        <option value="Resident">Resident</option>
                        <option value="Non-Resident">Non-Resident</option>
                        <option value="Minor">Minor</option>
                      </select>
                    </div>

                    <div>
                      <label>Non Individual Type</label>
                      <input
                        name="non_individual_type"
                        value={investment.non_individual_type}
                        onChange={handleTextOnlyChange}
                        className="w-full border p-2 rounded"
                        placeholder="If applicable (e.g., Trust, Company)"
                        inputMode="text"
                      />
                    </div>

                    <div>
                      <label>
                        Nationality <span className="required">*</span>
                      </label>
                      <select
                        name="nationality"
                        value={investment.nationality}
                        onChange={(e) => {
                          // if user chooses 'Other' you might let them type — keep simple here
                          handleChange(e);
                          validateField("nationality", e.target.value);
                        }}
                        onBlur={(e) =>
                          validateField("nationality", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.nationality ? "error-input" : ""
                        }`}
                      >
                        <option value="">Select Nationality</option>
                        <option value="Indian">Indian</option>
                        <option value="American">American</option>
                        <option value="British">British</option>
                        <option value="Australian">Australian</option>
                        <option value="Canadian">Canadian</option>
                        <option value="Other">Other</option>
                      </select>
                      <ErrorText field="nationality" />
                    </div>

                    <div>
                      <label>
                        Gender <span className="required">*</span>
                      </label>
                      <select
                        name="gender"
                        value={investment.gender}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label>Place of Birth</label>
                      <input
                        name="place_of_birth"
                        value={investment.place_of_birth}
                        onChange={handleTextOnlyChange}
                        className="w-full border p-2 rounded"
                        placeholder="City or place of birth"
                        inputMode="text"
                      />
                    </div>

                    <div>
                      <label>Country of Birth</label>
                      <input
                        name="country_of_birth"
                        value={investment.country_of_birth}
                        onChange={handleTextOnlyChange}
                        className="w-full border p-2 rounded"
                        placeholder="Country"
                        inputMode="text"
                      />
                    </div>

                    <div>
                      <label>
                        Father Name <span className="required">*</span>
                      </label>
                      <input
                        name="father_name"
                        value={investment.father_name}
                        onChange={handleTextOnlyChange}
                        onBlur={(e) =>
                          validateField("father_name", e.target.value)
                        }
                        className={`w-full border p-2 rounded ${
                          errors.father_name ? "error-input" : ""
                        }`}
                        placeholder="Full name of father"
                        inputMode="text"
                      />
                      <ErrorText field="father_name" />
                    </div>

                    <div>
                      <label>Mother Name</label>
                      <input
                        name="mother_name"
                        value={investment.mother_name}
                        onChange={handleTextOnlyChange}
                        className="w-full border p-2 rounded"
                        placeholder="Full name of mother (optional)"
                        inputMode="text"
                      />
                    </div>

                    <div>
                      <label>Spouse</label>
                      <input
                        name="spouse"
                        value={investment.spouse}
                        onChange={handleTextOnlyChange}
                        className="w-full border p-2 rounded"
                        placeholder="Spouse name (if applicable)"
                        inputMode="text"
                      />
                    </div>

                    <div>
                      <label>CKYC</label>
                      <select
                        name="ckyc"
                        value={investment.ckyc}
                        onChange={handleChange}
                        className="w-full border p-2 rounded"
                      >
                        <option value="">Select CKYC</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>

                  {activeStep === 5 && (
                    <button type="submit" className="btn btn-gps-primary mt-3">
                      Submit Investment
                    </button>
                  )}
                </Section>
              </form>
            </div>
      </div>
    </>
  );
}
